import React, { useState, useCallback, useEffect, useRef } from 'react';
import Input from '../../../ui/Input';
import Button from '../../../ui/Button';
import axiosInstance from '../../../api/axiosInstance';

// Credential configs for IMAP-based providers only
const CREDENTIAL_CONFIGS = {
  IMAP: [
    { key: 'hostname', label: 'IMAP Server', placeholder: 'imap.yourprovider.com', required: true, helperText: 'Your provider\'s incoming mail (IMAP) server' },
    { key: 'username', label: 'Email Address', placeholder: 'user@yourdomain.com', type: 'email', required: true },
    { key: 'password', label: 'Password', type: 'password', required: true, helperText: 'Use an app-specific password if your provider requires one' },
    { key: 'port', label: 'Port', placeholder: '993', type: 'number', helperText: '993 for SSL/TLS, 143 for STARTTLS' }
  ],
  CPANEL: [
    { key: 'hostname', label: 'IMAP Hostname', placeholder: 'mail.yourdomain.com', required: true },
    { key: 'username', label: 'Email Address', placeholder: 'user@yourdomain.com', type: 'email', required: true },
    { key: 'password', label: 'Password', type: 'password', required: true },
    { key: 'port', label: 'Port', placeholder: '993', type: 'number' }
  ],
  TITAN: [
    { key: 'hostname', label: 'IMAP Hostname', placeholder: 'imap.titan.email', required: true },
    { key: 'username', label: 'Email Address', placeholder: 'user@yourdomain.com', type: 'email', required: true },
    { key: 'password', label: 'Password', type: 'password', required: true },
    { key: 'port', label: 'Port', placeholder: '993', type: 'number' }
  ],
  YAHOO: [
    { key: 'adminEmail', label: 'Yahoo Email', placeholder: 'user@yahoo.com', type: 'email', required: true },
    { key: 'adminPassword', label: 'App Password', type: 'password', required: true, helperText: 'Generate from Yahoo Account Security page' }
  ],
  ZOHO: [
    { key: 'adminEmail', label: 'Admin Email', placeholder: 'admin@yourdomain.com', type: 'email', required: true },
    { key: 'adminPassword', label: 'Password', type: 'password', required: true },
    { key: 'domain', label: 'Domain', placeholder: 'yourdomain.com' }
  ]
};

const CONNECTION_STAGES = [
  { id: 'resolving', label: 'Resolving hostname' },
  { id: 'connecting', label: 'Connecting to server' },
  { id: 'authenticating', label: 'Authenticating' },
  { id: 'fetching', label: 'Fetching users' }
];

// OAuth provider brand colors and labels
const OAUTH_BRANDS = {
  microsoft: {
    label: 'Connect with Microsoft 365',
    bgColor: 'bg-[#2F2F2F] hover:bg-[#1a1a1a]',
    textColor: 'text-white',
    icon: (
      <svg className="w-5 h-5" viewBox="0 0 21 21" fill="none">
        <rect x="1" y="1" width="9" height="9" fill="#F25022"/>
        <rect x="11" y="1" width="9" height="9" fill="#7FBA00"/>
        <rect x="1" y="11" width="9" height="9" fill="#00A4EF"/>
        <rect x="11" y="11" width="9" height="9" fill="#FFB900"/>
      </svg>
    )
  },
  google: {
    label: 'Connect with Google Workspace',
    bgColor: 'bg-white hover:bg-gray-50 border border-gray-300',
    textColor: 'text-gray-700',
    icon: (
      <svg className="w-5 h-5" viewBox="0 0 24 24">
        <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 01-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" fill="#4285F4"/>
        <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
        <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/>
        <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
      </svg>
    )
  }
};

// Origin of the backend that serves the OAuth callback page (it sends the postMessage).
// Trust only messages from that origin to prevent credential injection.
const getTrustedOAuthOrigin = () => {
  const raw = process.env.REACT_APP_BUSINESS_SERVICE_URL;
  if (!raw) return null;
  try {
    return new URL(raw).origin;
  } catch {
    return null;
  }
};

export default function ConnectionStep({ source, credentials, onUpdate, onNext, showNotification }) {
  const [formData, setFormData] = useState(credentials || {});
  const [testing, setTesting] = useState(false);
  const [testStage, setTestStage] = useState(-1);
  const [testResult, setTestResult] = useState(null);
  const [error, setError] = useState(null);
  const [oauthConnecting, setOauthConnecting] = useState(false);
  const popupRef = useRef(null);
  const pollTimerRef = useRef(null);
  const messageHandlerRef = useRef(null);

  const isOAuthSource = source?.authMethod === 'oauth';
  const config = CREDENTIAL_CONFIGS[source?.id] || CREDENTIAL_CONFIGS.CPANEL;

  // Tear down all OAuth-related resources (popup, timer, listener).
  // Safe to call multiple times.
  const cleanupOAuth = useCallback(() => {
    if (pollTimerRef.current) {
      clearInterval(pollTimerRef.current);
      pollTimerRef.current = null;
    }
    if (messageHandlerRef.current) {
      window.removeEventListener('message', messageHandlerRef.current);
      messageHandlerRef.current = null;
    }
    if (popupRef.current && !popupRef.current.closed) {
      popupRef.current.close();
    }
    popupRef.current = null;
  }, []);

  // Clean up OAuth resources on unmount
  useEffect(() => {
    return cleanupOAuth;
  }, [cleanupOAuth]);

  // --- OAuth Flow ---

  const handleOAuthConnect = useCallback(async () => {
    // Tear down any prior attempt (stale timer/listener) before starting a new one
    cleanupOAuth();
    setOauthConnecting(true);
    setError(null);
    setTestResult(null);

    const trustedOrigin = getTrustedOAuthOrigin();
    if (!trustedOrigin) {
      setError('OAuth misconfigured: missing service URL');
      setOauthConnecting(false);
      return;
    }

    try {
      // 1. Get auth URL from backend
      const response = await axiosInstance.post(
        `/api/business/email-migration/oauth/authorize/${source.oauthProvider}`
      );

      if (!response.data.success) {
        throw new Error(response.data.error || 'Failed to initiate OAuth');
      }

      // 2. Open popup
      const width = 600, height = 700;
      const left = Math.round((window.screen.width - width) / 2);
      const top = Math.round((window.screen.height - height) / 2);
      const popup = window.open(
        response.data.authUrl,
        'migration_oauth',
        `width=${width},height=${height},left=${left},top=${top},resizable=yes,scrollbars=yes`
      );
      popupRef.current = popup;

      if (!popup) {
        setError('Popup was blocked. Please allow popups for this site and try again.');
        setOauthConnecting(false);
        return;
      }

      // 3. Listen for postMessage from callback page (only from trusted origin)
      const messageHandler = (event) => {
        if (event.origin !== trustedOrigin) return;
        if (event.data?.type !== 'MIGRATION_OAUTH_CALLBACK') return;

        if (event.data.success) {
          const oauthCredentials = {
            accessToken: event.data.accessToken,
            refreshToken: event.data.refreshToken,
            expiresAt: event.data.expiresAt,
            adminEmail: event.data.adminEmail
          };

          const connectionResult = {
            success: true,
            users: event.data.users || [],
            domains: event.data.domains || [],
            _enumNotSupported: false
          };

          setFormData(oauthCredentials);
          setTestResult(connectionResult);
          onUpdate(oauthCredentials, connectionResult);
        } else {
          setError(event.data.error || 'OAuth connection failed');
        }

        setOauthConnecting(false);
        cleanupOAuth();
      };
      messageHandlerRef.current = messageHandler;
      window.addEventListener('message', messageHandler);

      // 4. Monitor popup close (user cancelled)
      pollTimerRef.current = setInterval(() => {
        if (!popupRef.current || popupRef.current.closed) {
          setOauthConnecting(false);
          cleanupOAuth();
        }
      }, 1000);

    } catch (err) {
      setError(err.response?.data?.error || err.message || 'Failed to start OAuth');
      setOauthConnecting(false);
      cleanupOAuth();
    }
  }, [source, onUpdate, cleanupOAuth]);

  // --- IMAP Credential Flow ---

  const handleChange = useCallback((key, value) => {
    setFormData(prev => ({ ...prev, [key]: value }));
    setTestResult(null);
    setError(null);
  }, []);

  const isValid = config.filter(f => f.required).every(f => formData[f.key]?.trim());

  const handleTest = async () => {
    setTesting(true);
    setError(null);
    setTestResult(null);

    try {
      for (let i = 0; i < CONNECTION_STAGES.length; i++) {
        setTestStage(i);
        if (i < 3) await new Promise(r => setTimeout(r, 800));
      }

      const params = new URLSearchParams({ sourceType: source.id, ...formData });
      const response = await axiosInstance.get(`/api/business/email-migration/connect?${params}`);

      setTestStage(CONNECTION_STAGES.length);

      if (response.data.success) {
        setTestResult(response.data);
        onUpdate(formData, response.data);
      } else {
        setError(response.data.error || 'Connection failed');
      }
    } catch (err) {
      setError(err.response?.data?.error || err.message || 'Connection failed');
      setTestStage(-1);
    } finally {
      setTesting(false);
    }
  };

  // --- OAuth UI ---

  if (isOAuthSource) {
    const brand = OAUTH_BRANDS[source.oauthProvider] || OAUTH_BRANDS.microsoft;

    return (
      <div className="space-y-6">
        <div>
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Connect to {source?.name}</h3>
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
            Sign in with your {source?.name} admin account to securely connect
          </p>
        </div>

        {!testResult ? (
          <div className="space-y-4">
            {/* OAuth Connect Button */}
            <button
              onClick={handleOAuthConnect}
              disabled={oauthConnecting}
              className={`flex items-center justify-center gap-3 w-full max-w-sm px-6 py-3 rounded-lg font-medium text-sm transition-colors ${brand.bgColor} ${brand.textColor} disabled:opacity-50 disabled:cursor-not-allowed`}
            >
              {oauthConnecting ? (
                <svg className="w-5 h-5 animate-spin" fill="none" viewBox="0 0 24 24">
                  <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth={4} className="opacity-25" />
                  <path fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" className="opacity-75" />
                </svg>
              ) : brand.icon}
              {oauthConnecting ? 'Connecting...' : brand.label}
            </button>

            {/* Info */}
            <div className="flex items-start gap-2 p-3 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg max-w-sm">
              <svg className="w-5 h-5 text-blue-500 mt-0.5 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <div className="text-xs text-blue-700 dark:text-blue-300">
                <p className="font-medium">Secure OAuth Authentication</p>
                <p className="mt-0.5">
                  A popup will open for you to sign in with {source?.name}. Your credentials are never shared with us.
                  {source.oauthProvider === 'microsoft' && ' An admin account with User.Read.All permission is required.'}
                  {source.oauthProvider === 'google' && ' A Google Workspace super admin account is required.'}
                </p>
              </div>
            </div>
          </div>
        ) : (
          <div className="space-y-3">
            {/* Success */}
            <div className="flex items-start gap-2 p-3 bg-emerald-50 dark:bg-emerald-900/20 border border-emerald-200 dark:border-emerald-800 rounded-lg">
              <svg className="w-5 h-5 text-emerald-500 mt-0.5 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <div className="text-sm text-emerald-700 dark:text-emerald-300">
                <p className="font-medium">Connected via {source?.name}</p>
                <p>Signed in as {formData.adminEmail}</p>
                <p>Found {testResult.users?.length || 0} user(s) across {testResult.domains?.length || 0} domain(s)</p>
              </div>
            </div>

            <div className="flex items-center gap-2">
              <Button variant="primary" onClick={onNext}>Continue</Button>
              <Button variant="ghost" size="sm" onClick={() => { setTestResult(null); setFormData({}); }}>
                Reconnect
              </Button>
            </div>
          </div>
        )}

        {/* Error */}
        {error && (
          <div className="flex items-start gap-2 p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg max-w-sm">
            <svg className="w-5 h-5 text-red-500 mt-0.5 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
            </svg>
            <p className="text-sm text-red-700 dark:text-red-300">{error}</p>
          </div>
        )}
      </div>
    );
  }

  // --- IMAP Credential Form UI ---

  return (
    <div className="space-y-6">
      <div>
        <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Connect to {source?.name}</h3>
        <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
          Enter your credentials to connect to the source email server
        </p>
      </div>

      {/* Credential Form */}
      <div className="space-y-4 max-w-md">
        {config.map(field => (
          <Input
            key={field.key}
            label={field.label}
            type={field.type || 'text'}
            placeholder={field.placeholder}
            value={formData[field.key] || ''}
            onChange={(e) => handleChange(field.key, e.target.value)}
            required={field.required}
            helperText={field.helperText}
          />
        ))}
      </div>

      {/* Test Connection */}
      <div className="space-y-3">
        <Button
          variant="primary"
          onClick={handleTest}
          loading={testing}
          disabled={!isValid || testing}
        >
          {testing ? 'Testing...' : testResult ? 'Connected - Test Again' : 'Test Connection'}
        </Button>

        {/* Connection Stages */}
        {(testing || testStage >= 0) && (
          <div className="space-y-2 p-3 bg-gray-50 dark:bg-gray-800/50 rounded-lg">
            {CONNECTION_STAGES.map((stage, i) => (
              <div key={stage.id} className="flex items-center gap-2 text-sm">
                {i < testStage ? (
                  <svg className="w-4 h-4 text-emerald-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                ) : i === testStage && testing ? (
                  <svg className="w-4 h-4 text-orange-500 animate-spin" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <circle cx="12" cy="12" r="10" strokeWidth={2} strokeDasharray="20 40" />
                  </svg>
                ) : error && i === testStage ? (
                  <svg className="w-4 h-4 text-red-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                ) : (
                  <div className="w-4 h-4 rounded-full border-2 border-gray-300" />
                )}
                <span className={i <= testStage ? 'text-gray-700 dark:text-gray-300' : 'text-gray-400'}>
                  {stage.label}
                </span>
              </div>
            ))}
          </div>
        )}

        {/* Error */}
        {error && (
          <div className="flex items-start gap-2 p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
            <svg className="w-5 h-5 text-red-500 mt-0.5 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
            </svg>
            <p className="text-sm text-red-700 dark:text-red-300">{error}</p>
          </div>
        )}

        {/* Success */}
        {testResult && (
          <div className="space-y-3">
            <div className="flex items-start gap-2 p-3 bg-emerald-50 dark:bg-emerald-900/20 border border-emerald-200 dark:border-emerald-800 rounded-lg">
              <svg className="w-5 h-5 text-emerald-500 mt-0.5 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <div className="text-sm text-emerald-700 dark:text-emerald-300">
                <p className="font-medium">Connected successfully</p>
                <p>Found {testResult.users?.length || 0} user(s) across {testResult.domains?.length || 0} domain(s)</p>
              </div>
            </div>

            {testResult._enumNotSupported && (
              <div className="flex items-start gap-2 p-3 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg">
                <svg className="w-5 h-5 text-blue-500 mt-0.5 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <p className="text-sm text-blue-700 dark:text-blue-300">
                  {testResult.enumMessage || 'User listing not supported. Only the authenticated account is available for individual migration. Use CSV bulk import for multiple users.'}
                </p>
              </div>
            )}

            <Button variant="primary" onClick={onNext}>
              Continue
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}
